import { createHttpService } from "../ApiService";
import {
  TSpoc,
  TSpocPremise,
  TCreateSpocRequest,
  TUpdateSpocRequest,
  TPremise,
  TCreatePremiseRequest,
  TUpdatePremiseRequest,
} from "./spocPremise.interfaces";
import {
  spocListMock,
  premiseListMock,
} from "../../__mocks__/spocPremise/spocPremiseMock";

export const useSpocPremiseService = () => {
  const httpAPI = createHttpService();
  const baseSpocPremisePath = "";

  // Use mocks when VITE_APP_WITH_MOCK_DATA is set to "true"
  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const getSpocs = (instituteId?: string) => {
    if (appWithMockData) {
      // Filter by instituteId if provided
      if (instituteId) {
        return Promise.resolve(
          spocListMock.filter((spoc) => spoc.instituteId === instituteId),
        );
      }
      return Promise.resolve(spocListMock);
    }
    const path = instituteId
      ? `${baseSpocPremisePath}/spocs?instituteId=${instituteId}`
      : `${baseSpocPremisePath}/spocs`;
    return httpAPI.spocPremise.get<TSpoc[]>(path);
  };

  const createSpoc = (request: TCreateSpocRequest) => {
    if (appWithMockData) {
      // If setting as primary, unset other primary SPOCs for the same institute
      if (request.status === "primary") {
        spocListMock.forEach((spoc) => {
          if (
            spoc.instituteId === request.instituteId &&
            spoc.status === "primary"
          ) {
            spoc.status = "secondary";
          }
        });
      }

      const newSpoc: TSpoc = {
        id: `spoc-${Date.now()}`,
        ...request,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      spocListMock.push(newSpoc);
      return Promise.resolve(newSpoc);
    }
    return httpAPI.spocPremise.post<TSpoc>(
      `${baseSpocPremisePath}/spocs`,
      request,
    );
  };

  const updateSpoc = (id: string, request: TUpdateSpocRequest) => {
    if (appWithMockData) {
      const index = spocListMock.findIndex((spoc) => spoc.id === id);
      if (index === -1) {
        return Promise.reject(new Error("SPOC not found"));
      }

      const existingSpoc = spocListMock[index];

      // If setting as primary, unset other primary SPOCs for the same institute
      if (request.status === "primary" && existingSpoc.status !== "primary") {
        spocListMock.forEach((spoc) => {
          if (
            spoc.instituteId === existingSpoc.instituteId &&
            spoc.id !== id &&
            spoc.status === "primary"
          ) {
            spoc.status = "secondary";
          }
        });
      }

      const updatedSpoc: TSpoc = {
        ...existingSpoc,
        ...request,
        updatedAt: new Date().toISOString(),
      };
      spocListMock[index] = updatedSpoc;
      return Promise.resolve(updatedSpoc);
    }
    return httpAPI.spocPremise.patch<TSpoc>(
      `${baseSpocPremisePath}/spocs/${id}`,
      request,
    );
  };

  const deleteSpoc = (id: string) => {
    if (appWithMockData) {
      const index = spocListMock.findIndex((spoc) => spoc.id === id);
      if (index === -1) {
        return Promise.reject(new Error("SPOC not found"));
      }
      spocListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.spocPremise.delete<void>(
      `${baseSpocPremisePath}/spocs/${id}`,
    );
  };

  const getPremises = (instituteId?: string) => {
    if (appWithMockData) {
      // Filter by instituteId if provided
      if (instituteId) {
        return Promise.resolve(
          premiseListMock.filter(
            (premise) => premise.instituteId === instituteId,
          ),
        );
      }
      return Promise.resolve(premiseListMock);
    }
    const path = instituteId
      ? `${baseSpocPremisePath}/premises?instituteId=${instituteId}`
      : `${baseSpocPremisePath}/premises`;
    return httpAPI.spocPremise.get<TPremise[]>(path);
  };

  const getPremiseById = (id: string) => {
    if (appWithMockData) {
      const premise = premiseListMock.find((p) => p.id === id);
      if (!premise) {
        return Promise.reject(new Error("Premise not found"));
      }
      return Promise.resolve(premise);
    }
    return httpAPI.spocPremise.get<TPremise>(
      `${baseSpocPremisePath}/premises/${id}`,
    );
  };

  const createPremise = (request: TCreatePremiseRequest) => {
    if (appWithMockData) {
      const newPremise: TPremise = {
        id: `premise-${Date.now()}`,
        ...request,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      premiseListMock.push(newPremise);
      return Promise.resolve(newPremise);
    }
    return httpAPI.spocPremise.post<TPremise>(
      `${baseSpocPremisePath}/premises`,
      request,
    );
  };

  const updatePremise = (id: string, request: TUpdatePremiseRequest) => {
    if (appWithMockData) {
      const index = premiseListMock.findIndex((premise) => premise.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Premise not found"));
      }

      const updatedPremise: TPremise = {
        ...premiseListMock[index],
        ...request,
        updatedAt: new Date().toISOString(),
      };
      premiseListMock[index] = updatedPremise;
      return Promise.resolve(updatedPremise);
    }
    return httpAPI.spocPremise.patch<TPremise>(
      `${baseSpocPremisePath}/premises/${id}`,
      request,
    );
  };

  const deletePremise = (id: string) => {
    if (appWithMockData) {
      const index = premiseListMock.findIndex((premise) => premise.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Premise not found"));
      }
      premiseListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.spocPremise.delete<void>(
      `${baseSpocPremisePath}/premises/${id}`,
    );
  };

  const archivePremise = (id: string) => {
    if (appWithMockData) {
      const index = premiseListMock.findIndex((premise) => premise.id === id);
      if (index === -1) {
        return Promise.reject(new Error("Premise not found"));
      }
      premiseListMock[index] = {
        ...premiseListMock[index],
        status: "archived",
        updatedAt: new Date().toISOString(),
      };
      return Promise.resolve(premiseListMock[index]);
    }
    return httpAPI.spocPremise.patch<TPremise>(
      `${baseSpocPremisePath}/premises/${id}/archive`,
      {},
    );
  };

  const getSpocPremises = () => {
    if (appWithMockData) {
      return Promise.resolve([]);
    }
    return httpAPI.spocPremise.get<TSpocPremise[]>(`${baseSpocPremisePath}`);
  };

  return {
    getSpocs,
    createSpoc,
    updateSpoc,
    deleteSpoc,
    getPremises,
    getPremiseById,
    createPremise,
    updatePremise,
    deletePremise,
    archivePremise,
    getSpocPremises,
  };
};
